import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Home, MessageCircle } from "lucide-react";
import { waLink, SITE } from "@/lib/site-config";
import { trackEvent } from "@/lib/tracking";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ThankYou = () => {
  const location = useLocation();
  const fired = useRef(false);
  const [verified, setVerified] = useState(null); // null|true|false

  useEffect(() => {
    document.title = "Anfrage gesendet · " + SITE.brand.fullName;
    // Prevent indexing
    const meta = document.createElement("meta");
    meta.name = "robots"; meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  useEffect(() => {
    if (fired.current) return;
    const params = new URLSearchParams(location.search);
    const t = params.get("t");
    if (!t) { setVerified(false); return; }

    fired.current = true;
    axios.post(`${API}/leads/verify-conversion`, { token: t }).then(({ data }) => {
      if (data.status === "ok" && data.first_fire) {
        // Fire tracking events ONLY on first legitimate verification
        trackEvent("thank_you_page_view", {
          lead_id: data.lead_id,
          event_id: `ty_${data.lead_id}`,
          ...(data.attribution || {}),
        });
        trackEvent("generate_lead", {
          lead_id: data.lead_id,
          event_id: `lead_${data.lead_id}`,
          ...(data.attribution || {}),
        });
        setVerified(true);
      } else if (data.status === "already_verified") {
        // Duplicate reload – do NOT fire again
        setVerified(true);
      } else {
        setVerified(false);
      }
    }).catch(() => setVerified(false));
  }, [location.search]);

  return (
    <div className="min-h-screen bg-[#5C6B49] text-[#F3F1E8] relative grain hero-radial flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="relative max-w-2xl w-full text-center glass-strong rounded-3xl p-8 sm:p-12 border border-white/12"
        data-testid="thank-you-page"
      >
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#E8E5D0]/15 border border-[#E8E5D0]/30 mb-6">
          <CheckCircle2 size={40} className="text-[#E8E5D0]" />
        </div>
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#E8E5D0] mb-3">
          Anfrage erhalten
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
          Vielen Dank.
          <br />
          <span className="lime-gradient-text">Wir melden uns gleich.</span>
        </h1>
        <p className="mt-5 text-[#C9C6B4] max-w-md mx-auto leading-relaxed">
          Tobias meldet sich persönlich innerhalb von 24 Stunden – meist deutlich schneller per WhatsApp.
        </p>
        {verified === false && (
          <p className="mt-3 text-[11px] text-[#A8A58F]">
            Hinweis: Diese Seite wurde ohne gültige Anfrage geöffnet. Bitte über das Formular starten.
          </p>
        )}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <a href={waLink()} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold rounded-full px-6 py-3 transition-colors"
            data-testid="thanks-whatsapp-btn"
          >
            <MessageCircle size={16} /> Direkt auf WhatsApp schreiben
          </a>
          <Link to="/" className="inline-flex items-center justify-center gap-2 border border-white/15 hover:bg-black/[0.15] text-[#F3F1E8] font-bold rounded-full px-6 py-3 transition-colors" data-testid="thanks-home-btn">
            <Home size={16} /> Zurück zur Startseite
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default ThankYou;
